import { hu } from './hu';

// English mirrors the Hungarian structure; the type check keeps the keys in sync.
export const en: typeof hu = {
  app: {
    title: 'AppTransfer',
  },
  auth: {
    heroTitle: 'Hand over builds, with a download log.',
    heroText:
      'Upload a new version, the right people get an e-mail, and you always see who downloaded it and when — with a PDF certificate if you need one.',
    inviteOnly: 'For invited users only.',
    signInTitle: 'Sign in',
    signInText: 'Use the account your invitation was sent to.',
    signIn: 'Sign in',
    signInWithGoogle: 'Continue with Google',
    orWithEmail: 'or with e-mail',
    email: 'E-mail address',
    password: 'Password',
    newPassword: 'Choose a password',
    passwordHint: 'At least 8 characters.',
    name: 'Name',
    forgotPassword: 'Forgot password',
    gotInvite: 'Invited but no password yet?',
    activateLink: 'Activate your account',
    activateTitle: 'Activate your account',
    activateText:
      'If you were invited to a project, set a password here. For a Google address, signing in with Google is simpler.',
    activateEmailHint: 'Exactly the address the invitation was sent to.',
    activateButton: 'Activate account',
    activated: 'Account created. We sent you a confirmation e-mail.',
    resetTitle: 'Reset your password',
    resetText: "We'll e-mail you a link to set a new password.",
    sendResetLink: 'Send link',
    resetSent: 'If {email} has an account, the link will arrive in a few minutes.',
    backToSignIn: 'Back to sign in',
    signOut: 'Sign out',
    errors: {
      invalidCredentials: 'Wrong e-mail address or password.',
      notInvited: 'This address has not been invited. Ask the project owner for an invitation.',
      emailInUse: 'This address already has an account. Sign in or reset your password.',
      weakPassword: 'Password is too weak. Use at least 8 characters.',
      tooManyRequests: 'Too many attempts. Wait a few minutes and try again.',
      popupBlocked: 'Your browser blocked the Google window. Allow pop-ups for this site.',
      network: 'Cannot reach the server. Check your internet connection.',
      generic: 'Sign-in failed. Try again.',
    },
  },
  access: {
    deniedTitle: 'This account has no access',
    deniedText:
      '{email} is not on the invitation list. If you expect an invitation, ask the project owner to add you as a member, or sign in with the address the invitation was sent to.',
    checkAgain: 'Check again',
    unverifiedTitle: 'Confirm your e-mail address',
    unverifiedText: 'We sent a confirmation link to {email}. Click it, then come back here.',
    iVerified: "I've confirmed it",
    resend: 'Resend link',
    verificationSent: 'New confirmation link sent to {email}',
    maintenanceTitle: 'Maintenance',
    maintenanceText: 'AppTransfer is down for maintenance. Try again later.',
    useOtherAccount: 'Sign in with another account',
    admin: {
      title: 'Access',
      subtitle: 'Only the addresses listed here can use the app. Project owners can invite people too, by adding them as members.',
      loadFailed: 'Could not load access data. Refresh the page.',
      migrationTitle: 'Bring existing users over',
      migrationText:
        'Since the security update only invited addresses can sign in. {count} existing accounts have no invitation yet. This invites every project owner and member and converts old member and share-link data. Safe to run again.',
      migrationButton: 'Start migration',
      migrated: 'Done: {invited} addresses invited, {membersMigrated} members and {shareLinksMigrated} share links converted.',
      migrationFailed: 'Migration stopped. Run it again; finished parts are not duplicated.',
      inviteTitle: 'Invite',
      inviteText: 'A Google address can sign in right away; other addresses set a password via “Activate your account”.',
      inviteButton: 'Invite',
      invalidEmail: 'Enter a valid e-mail address.',
      invited: '{email} invited',
      people: 'People',
      search: 'Search by name or e-mail',
      empty: 'No matches',
      you: 'you',
      invitedOn: 'invited {date}',
      lastLogin: 'last signed in {date}',
      status: {
        admin: 'Admin',
        active: 'Active',
        invited: 'Invited',
        noAccess: 'No access',
      },
      grant: 'Grant access',
      revoke: 'Revoke access',
      revokeTitle: 'Revoke access',
      revokeText:
        '{email} loses access to every project immediately. Their memberships are kept, so re-inviting restores them.',
      revoked: 'Access revoked for {email}',
      makeAdmin: 'Make admin',
      makeAdminTitle: 'Grant admin rights',
      makeAdminText: '{email} will see and manage every project and can grant access.',
      madeAdmin: 'Admin rights granted',
      removeAdmin: 'Remove admin',
      removeAdminTitle: 'Remove admin rights',
      removeAdminText: '{email} will only see their own and shared projects.',
      removedAdmin: 'Admin rights removed',
    },
  },
  nav: {
    main: 'Main navigation',
    menu: 'Menu',
    account: 'Account',
    dashboard: 'Overview',
    projects: 'Projects',
    access: 'Access',
    settings: 'Settings',
    darkMode: 'Dark theme',
    lightMode: 'Light theme',
  },
  dashboard: {
    title: 'Overview',
    greeting: 'Hi, {name}',
    subtitle: 'The latest releases and downloads across your projects.',
    stats: {
      projects: 'Projects',
      projectsNote: '{owned} of them yours',
      releases: 'Releases',
      releasesNote: 'in total',
      downloads: 'Downloads',
      downloadsNote: 'in your projects',
    },
    recentReleases: 'Latest releases',
    noReleases: 'No releases yet',
    noReleasesText: 'When someone uploads a version to your projects, it shows up here.',
    favorites: 'Favourites',
    recentProjects: 'Recent projects',
    viewAll: 'View all',
    noProjects: 'No projects yet',
    noProjectsText: 'Create one, or wait until someone adds you to a project.',
    recentDownloads: 'Latest downloads',
  },
  projects: {
    title: 'Projects',
    subtitle: 'Your own projects and the ones shared with you.',
    adminSubtitle: 'As an admin you see every project.',
    new: 'New project',
    create: 'Create project',
    created: 'Project created',
    createFailed: 'Could not create the project. Try again.',
    loadFailed: 'Could not load projects.',
    name: 'Project name',
    description: 'Description',
    descriptionHint: 'People opening a share link see this too.',
    noDescription: 'No description',
    search: 'Search projects',
    filterByStatus: 'Filter by status',
    favoritesOnly: 'Favourites',
    favorite: 'Add to favourites',
    unfavorite: 'Remove from favourites',
    allProjects: 'All projects',
    mine: 'My projects',
    sharedWithMe: 'Shared with me',
    empty: 'No projects yet',
    emptyText: 'A project holds the versions of an app and the people who can download them.',
    noMatch: 'No projects match the filter.',
    updated: 'Updated {date}',
    status: {
      all: 'All',
      active: 'Active',
      archived: 'Archived',
      deleted: 'Deleted',
    },
  },
  projectDetail: {
    tabs: {
      releases: 'Releases',
      members: 'Members',
      sharing: 'Sharing',
      settings: 'Settings',
    },
    memberView: 'Member view',
    notFound: 'Project not found',
    notFoundText: 'It may have been deleted, or you do not have access to it.',
    loadFailed: 'Could not load the project',
    backToProjects: 'Back to projects',
    inactiveNotice: {
      archived: 'This project is archived. No new releases or members, and share links are paused.',
      deleted: 'This project is marked as deleted. Members cannot see it and share links are paused.',
    },
    editDetails: 'Name and description',
    editDetailsText: 'Shown to members and on the share page.',
    saved: 'Project saved',
    archive: 'Archive',
    archiveText: 'Hides it from active projects. Releases are kept and you can restore it later.',
    restore: 'Restore',
    restoreText: 'Makes it active again so members can reach it.',
    delete: 'Delete project',
    deleteText: 'Marks it as deleted. An admin can restore it later.',
    confirm: {
      archived: {
        title: 'Archive project',
        text: 'Archived projects take no new releases and their share links stop working.',
        button: 'Archive',
      },
      active: {
        title: 'Restore project',
        text: 'The project becomes active again. Its active share links will work again.',
        button: 'Restore',
      },
      deleted: {
        title: 'Delete project',
        text: 'Members will no longer see the project and its share links stop working. It can be restored later.',
        button: 'Delete',
      },
    },
    statusChanged: {
      active: 'Project restored',
      archived: 'Project archived',
      deleted: 'Project deleted',
    },
  },
  releases: {
    timeline: 'Releases, newest first',
    new: 'New release',
    newDescription: 'Project members get an e-mail.',
    version: 'Version',
    title: 'Title',
    titlePlaceholder: 'e.g. Sync fixes',
    notes: 'Changes',
    notesHint: 'What is in this version? Members and share-page visitors see it.',
    file: 'File',
    dropFile: 'Drop the file here',
    orBrowse: 'or click to browse',
    changeFile: 'click to replace',
    fileRequired: 'Choose a file.',
    publish: 'Publish',
    uploading: 'Uploading… {progress}%',
    published: 'v{version} published',
    publishFailed: 'Upload failed. Check your connection and try again.',
    notifying: 'Sending notifications…',
    notified: 'Notifications sent',
    notifyFailed: 'Notification e-mails were not sent.',
    notifyPartial: '{sent} notifications sent, {failed} could not be delivered.',
    notifyOnEdit: 'E-mail members about this change',
    resend: 'Resend notification',
    resendTitle: 'Resend notification',
    resendConfirm: 'Project members and admins get the e-mail about v{version} again.',
    resendButton: 'Send',
    latest: 'Latest',
    download: 'Download',
    downloadStarted: 'Download started',
    downloadFailed: 'Download failed. Try again.',
    downloadLog: 'Download log',
    edit: 'Edit release',
    saved: 'Release saved',
    saveFailed: 'Could not save. Try again.',
    delete: 'Delete',
    deleteTitle: 'Delete release',
    deleteConfirm: 'Release v{version} – {title} and its file will be deleted permanently. The download log is kept.',
    deleted: 'Release deleted',
    deleteFailed: 'Could not delete the release.',
    showMore: 'Show all',
    showLess: 'Show less',
    empty: 'No releases yet',
    emptyManager: 'Upload the first version; members get an e-mail.',
    emptyMember: 'Once the owner uploads a version, you can download it here.',
  },
  members: {
    explainer: 'Members can see and download releases. Adding someone also invites their address, so they can sign in.',
    add: 'Add member',
    addDescription: 'They get an invitation e-mail. A Google address can sign in right away, others set a password first.',
    addAndInvite: 'Add and invite',
    name: 'Name',
    email: 'E-mail address',
    invalidEmail: 'Enter a valid e-mail address.',
    alreadyMember: 'This address is already a member.',
    added: '{name} added',
    addFailed: 'Could not add the member.',
    sendingInvite: 'Sending invitation…',
    inviteSent: 'Invitation sent to {email}',
    inviteFailed: 'The invitation e-mail was not sent.',
    empty: 'No members yet',
    emptyText: 'Add the people who need to download the releases.',
    addedOn: 'added {date}',
    roles: {
      owner: 'Owner',
      member: 'Member',
    },
    remove: 'Remove {name}',
    removeTitle: 'Remove member',
    removeConfirm: '{name} will no longer see this project. Their access to other projects does not change.',
    removeButton: 'Remove',
    removed: '{name} removed',
    removeFailed: 'Could not remove the member.',
  },
  share: {
    explainer: 'A link for external partners. Before downloading they enter their name, e-mail and phone number.',
    create: 'New link',
    createText: 'The link gives access to every release in this project. Leave the fields empty for no limit.',
    createAndCopy: 'Create and copy',
    created: 'Link created',
    createFailed: 'Could not create the link.',
    validDays: 'Valid for (days)',
    maxDownloads: 'Maximum downloads',
    optional: 'Optional',
    empty: 'No share links',
    emptyText: 'Create one when you need to hand a build to someone without an account.',
    copy: 'Copy',
    copied: 'Link copied',
    copyFailed: 'Could not copy. Select the link and copy it by hand.',
    visitors: 'Downloads',
    deactivate: 'Stop link',
    deactivateTitle: 'Stop link',
    deactivateConfirm: 'The link stops working immediately. The download log is kept.',
    deactivated: 'Link stopped',
    meta: 'Created {created} — {count} downloads',
    limit: 'max {max}',
    expires: 'expires {date}',
    state: {
      ok: 'Active',
      inactive: 'Stopped',
      expired: 'Expired',
      limitReached: 'Used up',
      notFound: 'Not found',
    },
    visitorsTitle: 'Downloads via the link',
    visitorsText: 'Details entered by the visitors.',
    visitorsEmpty: 'Nobody has downloaded through this link yet.',
    contact: 'Contact',
  },
  downloads: {
    title: 'Download log',
    onlyYours: 'You only see your own downloads.',
    who: 'Who',
    what: 'What',
    when: 'When',
    ip: 'IP address',
    certificate: 'Certificate',
    empty: 'No downloads yet',
    emptyText: 'Every download is logged here, with a certificate.',
    loadFailed: 'Could not load the log.',
    total: '{count} downloads in total',
  },
  public: {
    sharedWithYou: 'Shared with you',
    formTitle: 'Your details',
    formText: 'The uploader sees who downloaded the file. Your details are used for nothing else.',
    name: 'Full name',
    email: 'E-mail address',
    phone: 'Phone number',
    invalidEmail: 'Enter a valid e-mail address.',
    continue: 'Continue to downloads',
    available: 'Available versions',
    changeDetails: 'Change details',
    noReleases: 'There is nothing to download in this project yet.',
    limitReached: 'This link has reached its download limit or expired.',
    unavailableTitle: 'This link is not available',
    unavailable: {
      notFound: 'The link is wrong or no longer exists. Ask the sender for a new one.',
      inactive: 'The uploader stopped this link. Ask the sender for a new one.',
      expired: 'The link has expired. Ask the sender for a new one.',
      limitReached: 'The link has reached its download limit. Ask the sender for a new one.',
      error: 'Could not load the link. Refresh the page.',
    },
    privacy: 'AppTransfer logs this download for the uploader.',
  },
  settings: {
    title: 'Settings',
    profile: 'Profile',
    language: 'Language',
    languageText: 'Language of the interface and dates.',
    theme: 'Theme',
    themeText: 'Light or dark appearance on this device.',
  },
  common: {
    loading: 'Loading…',
    working: 'Working…',
    cancel: 'Cancel',
    confirm: 'Confirm',
    close: 'Close',
    edit: 'Edit',
    saveChanges: 'Save changes',
    retry: 'Retry',
    admin: 'Admin',
    genericError: 'Something went wrong. Try again.',
  },
};
